import { FC, useCallback, useEffect } from "react"
import { useDispatch, useSelector } from "react-redux"
import { useNavigate } from "react-router"
import AuthLayout from "@/components/AuthLayout"
import Loader from "@/components/Loader"
import LoginSupport from "@/components/LoginSupport"
import Link from "@/components/shared/Link"
import { SpinnerSize } from "@/components/shared/Spinner"
import { PageRoutes } from "@/constants/routes"
import useQueryParams from "@/hooks/useQueryParams"
import { AppDispatch, RootState } from "@/store"
import { entraCallback } from "@/store/authSlice"
import { QueryParameter } from "@/types/index"
import { isEmpty, isNull } from "@/utils/lodash"
import "./index.scss"

const MISSING_PARAMS_MESSAGE =
   "This login link is incomplete. Please start again from the login screen."

const EntraCallback: FC = () => {
   const dispatch = useDispatch<AppDispatch>()
   const navigate = useNavigate()
   const { isAuthenticated, loadingEntraCallback, errorEntraCallback } =
      useSelector(({ authReducer }: RootState) => authReducer)
   const { isSuperAdmin } = useSelector(
      ({ userReducer }: RootState) => userReducer
   )
   const { getQueryParamByKey } = useQueryParams()

   const code = getQueryParamByKey(QueryParameter.CODE)
   const state = getQueryParamByKey(QueryParameter.STATE)
   const hasParams = !isEmpty(code) && !isEmpty(state)

   const handleBackToLogin = useCallback(() => {
      navigate(PageRoutes.LOGIN, { replace: true })
   }, [navigate])

   const hasCodeExchangeStarted =
      isAuthenticated || loadingEntraCallback || !isNull(errorEntraCallback)

   useEffect(() => {
      if (hasCodeExchangeStarted || !hasParams) {
         return
      }
      dispatch(entraCallback({ code, state }))
      window.history.replaceState(null, "", window.location.pathname)
   }, [dispatch, code, state, hasParams, hasCodeExchangeStarted])

   useEffect(() => {
      if (!isAuthenticated || isNull(isSuperAdmin)) {
         return
      }
      navigate(
         isSuperAdmin ? PageRoutes.CONTROL_PANEL : PageRoutes.FLOOR_OVERVIEW,
         { replace: true }
      )
   }, [isAuthenticated, isSuperAdmin, navigate])

   const errorMessage = isAuthenticated
      ? null
      : !hasParams
        ? MISSING_PARAMS_MESSAGE
        : errorEntraCallback

   if (isAuthenticated) {
      return <Loader size={SpinnerSize.LARGE} />
   }

   return (
      <AuthLayout
         iconName="guardUser"
         title="User login"
         instructions={
            <>
               <div className="entra-callback__instructions-description">
                  Completing your Microsoft sign in.
               </div>
               <LoginSupport />
            </>
         }
      >
         <>
            {loadingEntraCallback && (
               <div className="entra-callback__loading">
                  <Loader
                     size={SpinnerSize.LARGE}
                     fullHeight={false}
                  />
               </div>
            )}
            {errorMessage && (
               <>
                  <div className="entra-callback__error">{errorMessage}</div>
                  <Link
                     to={PageRoutes.LOGIN}
                     className="entra-callback__link"
                     onClick={handleBackToLogin}
                  >
                     Back to login screen
                  </Link>
               </>
            )}
         </>
      </AuthLayout>
   )
}

export default EntraCallback
